<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div class="space-y-6">
      <!-- Page Header -->
      <div>
        <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          Connections
        </h2>
        <p class="mt-1 text-sm text-gray-500">
          Manage your integrations with external platforms.
        </p>
      </div>

      <!-- WordPress Settings -->
      <div class="bg-white shadow sm:rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-medium leading-6 text-gray-900 flex items-center">
                <svg class="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2m0 19.5c-5.246 0-9.5-4.254-9.5-9.5S6.754 2.5 12 2.5s9.5 4.254 9.5 9.5-4.254 9.5-9.5 9.5m0-17c-4.142 0-7.5 3.358-7.5 7.5s3.358 7.5 7.5 7.5 7.5-3.358 7.5-7.5-3.358-7.5-7.5-7.5m.5 12h-1v-6h1v6zm0-7h-1V8h1v1.5z"/>
                </svg>
                WordPress
              </h3>
              <p class="mt-2 text-sm text-gray-500">
                Connect your WordPress site to automatically or manually publish articles.
              </p>
            </div>
            <div class="flex items-center">
              <span 
                :class="[
                  'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                  wpSettings ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ wpSettings ? 'Connected' : 'Not Connected' }}
              </span>
            </div>
          </div>

          <div class="mt-6">
            <WordPressSettings />
          </div>
        </div>
      </div>

      <!-- Image Source Settings -->
      <div class="bg-white shadow sm:rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-medium leading-6 text-gray-900 flex items-center">
                <svg class="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Image Source
              </h3>
              <p class="mt-2 text-sm text-gray-500">
                Choose your preferred image source for article generation.
              </p>
            </div>
          </div>

          <div class="mt-6">
            <div class="space-y-4">
              <div class="flex items-center space-x-4">
                <button
                  @click="setImageSource('unsplash')"
                  :class="[
                    'px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                    imageSource === 'unsplash' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  ]"
                >
                  Unsplash
                </button>
                <button
                  @click="setImageSource('pexels')"
                  :class="[
                    'px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                    imageSource === 'pexels' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  ]"
                >
                  Pexels
                </button>
              </div>
              <p class="text-sm text-gray-500">
                Current source: <span class="font-medium capitalize">{{ imageSource }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Future Integrations Placeholder -->
      <div class="bg-white shadow sm:rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg font-medium leading-6 text-gray-900">More Integrations Coming Soon</h3>
          <div class="mt-2 max-w-xl text-sm text-gray-500">
            <p>We're working on adding more integrations with popular platforms.</p>
          </div>
          <div class="mt-5">
            <button
              type="button"
              disabled
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-50 cursor-not-allowed"
            >
              Coming Soon
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import WordPressSettings from '../components/WordPressSettings.vue'
import { getWordPressSettings } from '../services/wordpressService'
import { useImageSourceStore } from '../stores/imageSource'
import { storeToRefs } from 'pinia'

const wpSettings = ref(null)
const imageSourceStore = useImageSourceStore()
const { imageSource } = storeToRefs(imageSourceStore)

onMounted(async () => {
  wpSettings.value = await getWordPressSettings()
})

function setImageSource(source) {
  imageSourceStore.setImageSource(source)
}
</script> 